import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { alertsAPI, notificationsAPI, getImageUrl } from '../utils/api';
import ImageModal from './ImageModal';
import ConfirmationModal from './ConfirmationModal';

/**
 * NotificationDetailModal
 * Shows full notification details including related alert information
 */
export default function NotificationDetailModal({
  isOpen,
  notification,
  onClose,
  onMarkAsRead,
  onViewAlert,
}) {
  const [showImageModal, setShowImageModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const severityColorConfig = {
    CRITICAL: {
      bg: 'bg-red-500/20',
      text: 'text-red-400',
      border: 'border-red-500/30',
      badge: 'bg-red-500 text-white',
    },
    HIGH: {
      bg: 'bg-orange-500/20',
      text: 'text-orange-400',
      border: 'border-orange-500/30',
      badge: 'bg-orange-500 text-white',
    },
    MEDIUM: {
      bg: 'bg-yellow-500/20',
      text: 'text-yellow-400',
      border: 'border-yellow-500/30',
      badge: 'bg-yellow-500 text-white',
    },
    LOW: {
      bg: 'bg-blue-500/20',
      text: 'text-blue-400',
      border: 'border-blue-500/30',
      badge: 'bg-blue-500 text-white',
    },
  };

  const channelIcons = {
    email: '📧',
    sms: '💬',
    push: '🔔',
    IN_APP: '📱',
    EMAIL: '📧',
    SMS: '💬',
    PUSH: '🔔',
  };

  const statusIcons = {
    pending: '⏳',
    sent: '✓',
    delivered: '✓✓',
    failed: '❌',
    PENDING: '⏳',
    DELIVERED: '✓✓',
    FAILED: '❌',
  };

  // Fetch alert details if notification has alertId
  const { data: alertData, isLoading: alertLoading } = useQuery({
    queryKey: ['alert-detail', notification?.alertId],
    queryFn: () =>
      notification?.alertId
        ? alertsAPI.getAlert(notification.alertId)
        : Promise.resolve(null),
    enabled: !!notification?.alertId && isOpen,
  });

  const alert = alertData?.data || notification?.alert;
  const colors = notification?.severity
    ? severityColorConfig[notification.severity]
    : severityColorConfig.MEDIUM;

  if (!isOpen || !notification) return null;

  const handleMarkAsRead = async () => {
    if (!notification.readAt) {
      await onMarkAsRead?.(notification.id);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    // Call parent handler or close modal
    onClose();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      >
        <div
          className="bg-slate-900/95 backdrop-blur border border-slate-700/50 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`sticky top-0 ${colors.bg} border-b ${colors.border} p-6 z-10`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}>
                    {notification.severity || 'MEDIUM'}
                  </span>
                  <span className="text-slate-400 text-sm">
                    {channelIcons[notification.channel] || '📬'} {notification.channel}
                  </span>
                </div>
                <h2 className={`text-xl font-bold ${colors.text}`}>
                  {notification.subject || notification.title || 'Notification'}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-200 text-2xl font-light"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                <div className="text-slate-400 text-sm font-semibold mb-2">Status</div>
                <div className="flex items-center gap-2">
                  <span className="text-xl">
                    {statusIcons[notification.status] || '◯'}
                  </span>
                  <span className="text-slate-200 font-medium">
                    {notification.status || 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                <div className="text-slate-400 text-sm font-semibold mb-2">Recipient</div>
                <div className="text-slate-200 font-medium truncate">
                  {notification.recipient || 'N/A'}
                </div>
              </div>
            </div>

            {/* Timestamps Section */}
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30 space-y-3">
              <div className="text-slate-400 text-sm font-semibold mb-2">Timestamps</div>

              {notification.sentAt && (
                <div className="flex justify-between items-start">
                  <span className="text-slate-400">Sent:</span>
                  <span className="text-slate-200">{formatDateTime(notification.sentAt)}</span>
                </div>
              )}

              {notification.deliveredAt && (
                <div className="flex justify-between items-start">
                  <span className="text-slate-400">Delivered:</span>
                  <span className="text-slate-200">{formatDateTime(notification.deliveredAt)}</span>
                </div>
              )}

              {notification.readAt && (
                <div className="flex justify-between items-start">
                  <span className="text-slate-400">Read:</span>
                  <span className="text-slate-200">{formatDateTime(notification.readAt)}</span>
                </div>
              )}

              {notification.createdAt && (
                <div className="flex justify-between items-start">
                  <span className="text-slate-400">Created:</span>
                  <span className="text-slate-200">{formatDateTime(notification.createdAt)}</span>
                </div>
              )}
            </div>

            {/* Message Section */}
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
              <div className="text-slate-400 text-sm font-semibold mb-3">Message</div>
              <p className="text-slate-200 leading-relaxed whitespace-pre-wrap">
                {notification.message || 'No message content'}
              </p>
            </div>

            {/* Alert Details Section (if alertId exists) */}
            {(alert || alertLoading) && (
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30 space-y-4">
                <div className="text-slate-400 text-sm font-semibold">Related Alert</div>

                {alertLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin">⟳</div>
                    <span className="ml-2 text-slate-400">Loading alert details...</span>
                  </div>
                ) : alert ? (
                  <>
                    {/* Alert Type and Location */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-slate-400 text-sm mb-1">Type</div>
                        <div className="text-slate-200 font-medium">
                          {alert.type?.replace(/_/g, ' ') || 'Unknown'}
                        </div>
                      </div>
                      {alert.subType && (
                        <div>
                          <div className="text-slate-400 text-sm mb-1">Subtype</div>
                          <div className="text-slate-200 font-medium">
                            {alert.subType.replace(/_/g, ' ')}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Location and Camera */}
                    <div className="grid grid-cols-2 gap-4">
                      {alert.site && (
                        <div>
                          <div className="text-slate-400 text-sm mb-1">Site</div>
                          <div className="text-slate-200 font-medium">{alert.site.name}</div>
                        </div>
                      )}
                      {alert.camera && (
                        <div>
                          <div className="text-slate-400 text-sm mb-1">Camera</div>
                          <div className="text-slate-200 font-medium">{alert.camera.name}</div>
                        </div>
                      )}
                    </div>

                    {/* Confidence and Status */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-slate-400 text-sm mb-1">Confidence</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-700 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{
                                width: `${(alert.confidence || 0) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-slate-200 font-medium">
                            {Math.round((alert.confidence || 0) * 100)}%
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-400 text-sm mb-1">Status</div>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            alert.status === 'RESOLVED'
                              ? 'bg-green-500/20 text-green-400'
                              : alert.status === 'ACKNOWLEDGED'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {alert.status}
                        </span>
                      </div>
                    </div>

                    {/* Snapshot Image */}
                    {(alert.snapshotUrl || alert.thumbnailUrl) && (
                      <div>
                        <div className="text-slate-400 text-sm mb-2">Snapshot</div>
                        <button
                          onClick={() => setShowImageModal(true)}
                          className="w-full overflow-hidden rounded-lg hover:opacity-75 transition-opacity"
                        >
                          <img
                            src={getImageUrl(alert.snapshotUrl || alert.thumbnailUrl)}
                            alt="Alert snapshot"
                            className="w-full h-auto max-h-64 object-cover"
                          />
                        </button>
                        <div className="text-slate-400 text-xs mt-1 text-center">
                          Click to zoom
                        </div>
                      </div>
                    )}

                    {/* Worker/Contractor Info */}
                    {(alert.workerName || alert.workerBadge || alert.contractor) && (
                      <div className="bg-slate-700/30 rounded p-3">
                        {alert.workerName && (
                          <div className="text-slate-300">
                            <span className="text-slate-400">Worker:</span> {alert.workerName}
                          </div>
                        )}
                        {alert.workerBadge && (
                          <div className="text-slate-300">
                            <span className="text-slate-400">Badge:</span> {alert.workerBadge}
                          </div>
                        )}
                        {alert.contractor && (
                          <div className="text-slate-300">
                            <span className="text-slate-400">Contractor:</span> {alert.contractor}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* Actions Footer */}
          <div className="sticky bottom-0 bg-slate-800/50 border-t border-slate-700/30 p-4 flex gap-3">
            {!notification.readAt && (
              <button
                onClick={handleMarkAsRead}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                ✓ Mark as Read
              </button>
            )}

            {alert && (
              <button
                onClick={() => onViewAlert?.(alert.id)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
              >
                View Full Alert
              </button>
            )}

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
            >
              Delete
            </button>

            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal for alert snapshot */}
      {alert && (alert.snapshotUrl || alert.thumbnailUrl) && (
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          imageUrl={getImageUrl(alert.snapshotUrl || alert.thumbnailUrl)}
          alt="Alert snapshot"
          title={`${alert.type?.replace(/_/g, ' ')} - ${alert.camera?.name || 'Camera'}`}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </>
  );
}
